import React, { JSXElementConstructor, ReactElement, useEffect, useState } from "react";
import { connect } from "react-redux";
import { injectIntl, intlShape } from "@ai360/core/intl";
import classnames from "classnames";
import { parse } from "qs";

import { Loader } from "~/core";
import { getErrorMessages } from "~/i18n-error-messages";
import { messages as globalMessages } from "~/i18n-messages";
import { messages as loginMessages } from "../i18n-messages";
import { ThemeRoot } from "~/theme";
import { APIErrorWithCode, isSkyUrl, urlBase } from "@ai360/core";

import EULA from "./eula";
import LoginForm from "./login-form";
import PasswordEntry from "./password-entry";
import RecoverPassword from "./recover-password";
import ResetPassword from "./reset-password";
import SecurityQuestion from "./security-question";
import SecurityMessage from "./security-message";
import SelectCompany from "./select-company";
import SelectSecurityQuestion from "./select-security-question";
import * as actions from "../actions";
import * as selectors from "../selectors";

import "./login.css";

const { LoginState, LoginErrors } = actions;
interface ILoginPage_Props {
    error?: any;
    location?: any;
    isProcessing?: boolean;
    loginState?: unknown;
    onAutoLogin: (query: string) => void;
    onSetSecurityMessage: (code: string) => void;
    intl: intlShape;
    history?: any;
}

const LoginPage_ = (props: ILoginPage_Props): JSX.Element => {
    const {
        error,
        location,
        isProcessing,
        loginState,
        onAutoLogin,
        onSetSecurityMessage,
        intl,
        history,
    } = props;
    const { formatMessage } = intl;

    let versionPromise: Promise<boolean> | null = null;

    //state variables
    const [errMessage, setErrMessage] = useState<any>("");
    const [shaking, setShaking] = useState<boolean>(false);

    function _clearError() {
        setErrMessage("");
    }

    useEffect(() => {
        if (isProcessing) {
            _clearError();
        }
    }, [isProcessing]);

    useEffect(() => {
        if (error) {
            handleError(error);
        }
    }, [error]);

    useEffect(() => {
        _clearError();
    }, [loginState]);

    useEffect(() => {
        checkForUIupdate();
        const query = location && parse(location.search.slice(1));
        onAutoLogin(query && query.key);

        const timerId = setInterval(() => checkForUIupdate(), 3600000); // 1 hour
        return () => clearInterval(timerId);
    }, []);

    function handleError(err) {
        let errMessage: string | ReactElement<unknown, string | JSXElementConstructor<any>> = "";
        if (err instanceof APIErrorWithCode) {
            const { apiResultObj } = err;
            if (
                apiResultObj &&
                (apiResultObj as Record<string, any>).model &&
                typeof (apiResultObj as Record<string, any>).model.securityCode === "number"
            ) {
                onSetSecurityMessage((apiResultObj as Record<string, any>).model.securityCode);
            } else {
                errMessage = getErrorMessages(formatMessage, err);
            }
        } else if (err.code === LoginErrors.INVALID_PARAMETER) {
            errMessage = formatMessage(loginMessages.validEmail);
        } else if (typeof err === "string" || React.isValidElement(err)) {
            errMessage = err;
        } else if (err && err.id != null && err.defaultMessage != null) {
            errMessage = formatMessage(err);
        } else if (err && typeof err.message === "string" && !/^{.*}$/.test(err.message)) {
            errMessage = err.message;
        } else if (err && err.message) {
            const message = JSON.parse(err.message);
            if (message && typeof message.model === "string") {
                errMessage = message.model;
            } else if (message && message.model && typeof message.model.securityCode === "number") {
                onSetSecurityMessage(message.model.securityCode);
            }
        } else {
            errMessage = formatMessage(globalMessages.unknownError);
        }

        setShaking(true);
        setErrMessage(errMessage);

        setTimeout(() => setShaking(false), 250);
    }

    const checkForUIupdate = (forceCheck = false): Promise<boolean> => {
        if (!versionPromise || forceCheck) {
            const promiseForVersion = fetch(`deployed-version.json?${Date.now()}`)
                .then((response) => response.json())
                .then((versionObj) => {
                    const expectedVersion: string = versionObj.uiVersion.split("|")[0];
                    const actualVersion: string = window.process_env.__BUILD_STR__.split("|")[0];
                    versionPromise = null;

                    if (expectedVersion.trim() !== actualVersion.trim()) {
                        console.warn(
                            `Refreshing UI, expecting '${expectedVersion}', saw '${actualVersion}'`
                        );
                        window.location.href =
                            (!isSkyUrl
                                ? window.process_env.PUBLIC_URL
                                : window.location.origin + urlBase) + `login?refresh=${Date.now()}`;
                        return true;
                    }

                    return false;
                });

            versionPromise = promiseForVersion;
            return promiseForVersion;
        }

        return versionPromise;
    };

    let presentation = null;
    const loginBoxClasses = [
        "login-box",
        {
            "login-err-animation": shaking,
        },
    ];

    const errorHandling = {
        onError: (err) => handleError(err),
    };
    switch (loginState) {
        case LoginState.SELECT_COMPANY:
            presentation = <SelectCompany {...errorHandling} isQuickChanger={false} />;
            break;
        case LoginState.LOGIN_FORM:
            presentation = (
                <LoginForm {...errorHandling} doVersionCheck={() => checkForUIupdate(true)} />
            );
            break;
        case LoginState.PASSWORD_ENTRY:
            presentation = (
                <PasswordEntry {...errorHandling} doVersionCheck={() => checkForUIupdate(true)} />
            );
            break;
        case LoginState.RECOVER_PASSWORD:
            presentation = <RecoverPassword {...errorHandling} />;
            break;
        case LoginState.RESET_PASSWORD:
            presentation = <ResetPassword {...errorHandling} history={history} />;
            break;
        case LoginState.SECURITY_QUESTION:
            presentation = <SecurityQuestion {...errorHandling} />;
            break;
        case LoginState.SELECT_SECURITY_QUESTION:
            presentation = <SelectSecurityQuestion {...errorHandling} />;
            break;
        case LoginState.SECURITY_MESSAGE:
            presentation = <SecurityMessage />;
            break;
        case LoginState.EULA:
            presentation = <EULA />;
            break;
        default:
            presentation = null;
            loginBoxClasses.push("hidden");
    }
    const errMsgEl = Array.isArray(errMessage)
        ? errMessage.map((msg, idx) => <p key={idx}>{msg}</p>)
        : errMessage;
    return (
        <ThemeRoot className="login-root">
            {isProcessing ? <Loader /> : null}
            <div className="login-page-container">
                <div className={classnames(loginBoxClasses)}>
                    <div className="login-logo-div"></div>
                    <div className="validation-summary-errors">{errMsgEl}</div>
                    {presentation}
                </div>
            </div>
        </ThemeRoot>
    );
};

const mapStateToProps = (state): Partial<ILoginPage_Props> => {
    return {
        error: selectors.getError(state),
        isProcessing: selectors.isProcessing(state),
        loginState: selectors.getLoginState(state),
    };
};

const mapDispatchToProps = (dispatch, ownProps): Partial<ILoginPage_Props> => {
    return {
        onAutoLogin: (key) => dispatch(actions.autoLogin(key, ownProps.history)),
        onSetSecurityMessage: (code) => dispatch(actions.setSecurityMessage(code)),
    };
};

export const LoginPage = connect(mapStateToProps, mapDispatchToProps)(injectIntl(LoginPage_));
